import { CONSENT_SHEET, CONSENT_VERSION } from '@aera/content/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AeraMark, Spinner } from '../components/ui.js';
import { CrossBorderText, ProcessingText, consentVars, versionLine } from '../client/ConsentText.js';
import { clientApi, type PublicConfig } from '../lib/api.js';

/** The configured privacy email and retention, when the API answered with them. */
const usable = (c: unknown): PublicConfig | null =>
  !!c &&
  typeof c === 'object' &&
  typeof (c as PublicConfig).privacyEmail === 'string' &&
  (c as PublicConfig).privacyEmail !== '' &&
  Number.isInteger((c as PublicConfig).retentionMonths)
    ? (c as PublicConfig)
    : null;

/**
 * The full notice in both languages, linked from the consent screens. Same order, same content, and the same
 * privacy email, retention and storage region as the consent sheet (the API's configuration, not the defaults).
 */
export function PrivacyPage() {
  const { t } = useTranslation();
  // undefined while loading; null when the API did not answer, and the defaults are shown.
  const [config, setConfig] = useState<PublicConfig | null>();
  // The tab follows the page's language, as the questionnaire and the brief do.
  useEffect(() => {
    document.title = `${t('privacy.pageTitle')} · Aera`;
  }, [t]);
  useEffect(() => {
    let live = true;
    clientApi.publicConfig().then(
      (c) => live && setConfig(usable(c)),
      () => live && setConfig(null),
    );
    return () => {
      live = false;
    };
  }, []);
  if (config === undefined) {
    return (
      <main className="flex min-h-[60svh] items-center justify-center">
        <Spinner />
      </main>
    );
  }
  const vars = consentVars(config);
  return (
    <main className="mx-auto max-w-[720px] px-4 pb-16 pt-8">
      <div className="flex items-center gap-2">
        <AeraMark />
        <h1 className="text-[22px] font-semibold">{t('privacy.pageTitle')}</h1>
      </div>
      <p className="mt-2 text-[14px] text-ink-2">{t('privacy.both')}</p>
      {(['zh', 'en'] as const).map((lang) => (
        <section key={lang} lang={lang === 'zh' ? 'zh-CN' : 'en'} className="mt-10 border-t border-hair pt-6">
          <h2 className="text-[18px] font-semibold">{CONSENT_SHEET.processing.title[lang]}</h2>
          <div className="mt-3">
            <ProcessingText lang={lang} vars={vars} />
          </div>
          <h2 className="mt-8 text-[18px] font-semibold">{CONSENT_SHEET.crossBorder.title[lang]}</h2>
          <div className="mt-3 space-y-3">
            <CrossBorderText lang={lang} vars={vars} llmMode="cloud" />
            <p className="text-[14px] text-ink-2">{renderLocalNote(lang)}</p>
            <CrossBorderText lang={lang} vars={vars} llmMode="local" />
          </div>
          <p className="mt-4 text-[13px] text-ink-2">{versionLine(lang, CONSENT_VERSION, vars)}</p>
        </section>
      ))}
    </main>
  );
}

const renderLocalNote = (lang: 'zh' | 'en') =>
  lang === 'zh' ? '若模型运行于 Aera 自有服务器，第二页内容改为：' : "When the model runs on Aera's own server, page 2 reads instead:";
